//@flow
import React, {useEffect} from 'react';
import {Link, useHistory, useParams} from 'react-router-dom';

import {
  makeStyles,
  Typography,
  IconButton,
  FormControlLabel,
} from '@material-ui/core';
import {colors} from '../../../../../../assets/styles/colors';
import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import {useForm} from 'react-hook-form';
import CustomTextField from '../../../../../../elements/CustomTextField/CustomTextField';
import {
  useStoreLocationActions,
  useStoreLocation,
} from '../../../../utils/location';
import InlineLoader from '../../../../../../elements/LoadingSpinner/InlineLoader';
import {ArrowBack} from '@material-ui/icons';
import IosSwitch from '../../../../../../elements/IosSwitch/IosSwitch';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100%',
    maxWidth: '1200px',
    position: 'relative',
  },
  loader: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    background: 'rgba(255,255,255,.5)',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  header: {
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'flex-start',

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      alignItems: 'center',
    },
  },
  title: {
    fontSize: '1rem',
    fontWeight: '600',
    color: colors.grey900,
  },
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '1rem',

    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: '70% 30%',
    },
  },
  backButton: {
    background: 'white',
    padding: '4px',
    margin: '0 16px 0 0',
    boxShadow:
      '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
  },
  sectionTitle: {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: colors.grey900,
  },
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    gap: '1rem',
    position: 'relative',
    padding: '1rem',
    borderRadius: '12px',
    background: 'white',
    boxShadow:
      '0px 0.5px 2px 0px rgba(96, 97, 112, 0.16), 0px 0px 1px 0px rgba(40, 41, 61, 0.08);',
  },
  rightContent: {
    display: 'none',
    [theme.breakpoints.up('lg')]: {
      height: 'min-content',
      position: 'sticky',
      top: '5.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      width: '100%',
    },
  },
  submitButton: {
    display: 'block',
    maxWidth: '200px',
    padding: '0.5rem 1.5rem',
  },
  captionText: {
    fontSize: '0.875rem',
    color: colors.grey600,
    marginBottom: '.25rem',
    fontWeight: 500,
  },
}));

const FORM_FIELDS = {
  NAME: 'name',
  ADDRESS: 'address',
  MAP_URL: 'map_url',
  SCHEDULE: 'schedule',
  PHONE: 'phone',
  ENABLED: 'enabled',
};

export default function LocationForm() {
  const classes = useStyles();
  const {register, errors, handleSubmit, watch, reset, setValue} = useForm();

  const {
    createStoreLocation,
    updateStoreLocation,
    loading,
  } = useStoreLocationActions();

  const history = useHistory();
  const {id} = useParams();

  const {location} = useStoreLocation(id === 'new' ? undefined : id);

  const onSubmit = data => {
    if (id === 'new') {
      createStoreLocation({
        name: data[FORM_FIELDS.NAME],
        address: data[FORM_FIELDS.ADDRESS],
        mapUrl: data[FORM_FIELDS.MAP_URL] || undefined,
        schedule: data[FORM_FIELDS.SCHEDULE] || undefined,
        phone: data[FORM_FIELDS.PHONE] || undefined,
        enabled: !!data[FORM_FIELDS.ENABLED],
      }).then(result => {
        if (!result) return;

        history.push('/my-store/pages/home-page');
      });
    } else {
      updateStoreLocation(id ?? '', {
        name: data[FORM_FIELDS.NAME],
        address: data[FORM_FIELDS.ADDRESS],
        mapUrl: data[FORM_FIELDS.MAP_URL] || undefined,
        schedule: data[FORM_FIELDS.SCHEDULE] || undefined,
        phone: data[FORM_FIELDS.PHONE] || undefined,
        enabled: !!data[FORM_FIELDS.ENABLED],
      }).then(result => {
        if (!result) return;

        history.push('/my-store/pages/home-page');
      });
    }
  };

  useEffect(() => {
    register(FORM_FIELDS.ENABLED);
  }, [register]);

  useEffect(() => {
    if (!location) return;

    reset({
      [FORM_FIELDS.NAME]: location.name,
      [FORM_FIELDS.ADDRESS]: location.address,
      [FORM_FIELDS.MAP_URL]: location.mapUrl,
      [FORM_FIELDS.SCHEDULE]: location.schedule,
      [FORM_FIELDS.PHONE]: location.phone,
      [FORM_FIELDS.ENABLED]: location.enabled,
    });
  }, [location, reset, setValue]);

  const enabled = watch(FORM_FIELDS.ENABLED);

  return (
    <form className={classes.container} onSubmit={handleSubmit(onSubmit)}>
      {loading && (
        <div className={classes.loader}>
          <InlineLoader size={40} />
        </div>
      )}

      <div className={classes.leftContent}>
        <header className={classes.header}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
            }}
          >
            <IconButton
              className={classes.backButton}
              component={Link}
              to="/my-store/pages/home-page"
            >
              <ArrowBack />
            </IconButton>
            <Typography className={classes.title}>
              Crear nueva ubicación
            </Typography>
          </div>
          <FormControlLabel
            labelPlacement="start"
            control={
              <div className={classes.switch}>
                <IosSwitch
                  checked={!!enabled}
                  onChange={() => {
                    setValue(FORM_FIELDS.ENABLED, !enabled);
                  }}
                  name={FORM_FIELDS.ENABLED}
                />
              </div>
            }
            classes={{
              label: classes.switchLabel,
            }}
            label={enabled ? 'Habilitado' : 'Deshabilitado'}
          />
        </header>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            1. Nombre de la ubicación
          </Typography>
          <CustomTextField
            label=""
            name={FORM_FIELDS.NAME}
            placeholder="Ej. Sucursal Reforma, Almacén, etc."
            inputRef={register({
              maxLength: {
                value: 40,
                message: 'Máximo 40 caracteres',
              },
              required: 'Campo requerido',
            })}
            error={errors[FORM_FIELDS.NAME]}
            helperText={
              errors[FORM_FIELDS.NAME] && errors[FORM_FIELDS.NAME].message
            }
          />
        </section>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            2. Dirección de local
          </Typography>
          <CustomTextField
            label=""
            name={FORM_FIELDS.ADDRESS}
            placeholder="Av. Paseo de la Reforma 123"
            inputRef={register({
              maxLength: {
                value: 40,
                message: 'Máximo 40 caracteres',
              },
              required: 'Campo requerido',
            })}
            error={errors[FORM_FIELDS.ADDRESS]}
            helperText={
              errors[FORM_FIELDS.ADDRESS] && errors[FORM_FIELDS.ADDRESS].message
            }
          />
        </section>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            3. Horario (opcional)
          </Typography>
          <CustomTextField
            label=""
            name={FORM_FIELDS.SCHEDULE}
            placeholder="Lunes a viernes de 9:00 a 18:00"
            inputRef={register()}
            error={errors[FORM_FIELDS.SCHEDULE]}
            helperText={
              errors[FORM_FIELDS.SCHEDULE] &&
              errors[FORM_FIELDS.SCHEDULE].message
            }
          />
        </section>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            4. Teléfono (opcional)
          </Typography>
          <CustomTextField
            label=""
            name={FORM_FIELDS.PHONE}
            placeholder="55 1234 5678"
            inputRef={register()}
            error={errors[FORM_FIELDS.PHONE]}
            helperText={
              errors[FORM_FIELDS.PHONE] && errors[FORM_FIELDS.PHONE].message
            }
          />
        </section>

        <section className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            5. URL de Google Maps (opcional)
          </Typography>
          <CustomTextField
            label=""
            name={FORM_FIELDS.MAP_URL}
            placeholder="https://goo.gl/maps/..."
            inputRef={register()}
            error={errors[FORM_FIELDS.MAP_URL]}
            helperText={
              errors[FORM_FIELDS.MAP_URL] && errors[FORM_FIELDS.MAP_URL].message
            }
          />
        </section>

        <footer className={classes.actions}>
          <CustomButton
            className={classes.submitButton}
            color="primary"
            variant="contained"
            fullWidth
            type="submit"
          >
            Guardar
          </CustomButton>
        </footer>
      </div>
    </form>
  );
}
